<script setup lang="ts">
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
// biome-ignore lint/correctness/noUnusedImports: used via tag <KioskLayout> in <template>
import KioskLayout from '@/app/layouts/kiosk/KioskLayout.vue';
import { router } from '@/app/router';
import { sentryService } from '@/services/telemetry/sentry';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
// biome-ignore lint/correctness/noUnusedImports: used via tag <KioskPaymentChoice> in <template>
import KioskPaymentChoice from './terminal/KioskPaymentChoice.vue';

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const { t } = useI18n();

const paymentSteps = {
  card: 'payment-terminal',
  voucher: 'payment-voucher',
} as const;

type PaymentType = keyof typeof paymentSteps;

interface PaymentTypePublicApi {
  handlePaymentInitError: (error: unknown, paymentType: string) => void;
  navigateToPaymentStep: (paymentType: string) => void;
  selectPaymentType: (paymentType: PaymentType) => void;
  trackStepEntry: () => void;
}

function trackStepEntry(): void {
  try {
    sentryService.addBreadcrumb(
      'payment',
      'User entered payment type selection',
      { step: 'payment-type' }
    );
    sentryService.setCurrentStep('payment-type');
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.warn('[Sentry] Tracking failed:', message);
  }
}

function handlePaymentInitError(error: unknown, paymentType: string): void {
  sentryService.captureException(error as Error, {
    tags: {
      component: 'PaymentType',
      payment_type: paymentType,
      action: 'payment_init',
    },
    extra: {
      selectedPaymentType: paymentType,
      currentRoute: router.path,
    },
    level: 'error',
  });

  console.error('[PaymentType] Payment initialization error:', error);
}

function navigateToPaymentStep(paymentType: string): void {
  const targetStep = paymentSteps[paymentType as PaymentType];
  if (!targetStep) {
    handlePaymentInitError(
      new Error(`Unknown payment type: ${paymentType}`),
      paymentType
    );
    return;
  }

  sentryService.addBreadcrumb('navigation', `Navigating to ${targetStep}`, {
    from: 'payment-type',
    to: targetStep,
    payment_type: paymentType,
  });

  router.redirectToStep(targetStep);
}

function selectPaymentType(paymentType: PaymentType): void {
  try {
    sentryService.addBreadcrumb(
      'payment',
      `Payment type selected: ${paymentType}`,
      {
        payment_type: paymentType,
        timestamp: new Date().toISOString(),
      }
    );
    sentryService.setTag('payment_type', paymentType);
    sentryService.incrementCounter('payment.type_selected', 1, {
      payment_type: paymentType,
    });
    navigateToPaymentStep(paymentType);
  } catch (error) {
    handlePaymentInitError(error, paymentType);
  }
}

const publicApi: PaymentTypePublicApi = {
  handlePaymentInitError,
  navigateToPaymentStep,
  selectPaymentType,
  trackStepEntry,
};

onMounted(() => {
  exposeToGlobalScope(publicApi, 'PaymentType');
  trackStepEntry();
});

defineExpose(publicApi);
</script>

<template>
  <KioskLayout :is-centered="true">
    <div class="PaymentType" data-testid="payment-type-root">
      <h1 class="kiosk-title">{{ t('PaymentType.title') }}</h1>

      <div class="PaymentType__options">
        <KioskPaymentChoice
          :caption="t('PaymentType.optionCardCaption')"
          kind="card"
          test-id="payment-type-card"
          :title="t('PaymentType.optionCardTitle')"
          @select="selectPaymentType('card')"
        />
        <KioskPaymentChoice
          :caption="t('PaymentType.optionVoucherCaption')"
          kind="voucher"
          test-id="payment-type-voucher"
          :title="t('PaymentType.optionVoucherTitle')"
          @select="selectPaymentType('voucher')"
        />
      </div>
    </div>
  </KioskLayout>
</template>

<style scoped lang="scss">
.PaymentType__options {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
}
</style>
